import React, {Component} from 'react'
import {Box, Grid} from "@material-ui/core/index";
import {ClientesService, CobranzasService, EmailService, ListadosService, MastersService, VendedoresService} from "services";
import {withSnackbar} from "notistack";
import MomentUtils from "@date-io/moment";
import 'moment/locale/es';
import {DateRangeControl} from "components/helpers/dateRange/dateRange.control";
import {MainContext} from "contexts/main.context";
import {MailDialog} from "components/helpers/dialogs/mail.dialog";
import SpeedDial from "@material-ui/lab/SpeedDial/SpeedDial";
import SpeedDialAction from "@material-ui/lab/SpeedDialAction/SpeedDialAction";
import PDFIcon from "@material-ui/icons/PictureAsPdf";
import DownloadIcon from "@material-ui/icons/GetAppRounded";
import ShowIcon from "@material-ui/icons/DesktopWindowsRounded";
import PrintIcon from "@material-ui/icons/PrintRounded";
import EmailIcon from "@material-ui/icons/EmailRounded";
import printJS from 'print-js'
import {PDFDialog} from "components/helpers/dialogs/pdf.dialog";
import {ReactGrid} from "components/helpers/reactGrid/reactGrid";
import {CobranzaStatusFormatter, getCobranzaStatusInfo, sortDate, sortNumeric} from "assets/utils";
import {Checkbox, FormControlLabel, IconButton, ListItemText, MenuItem, TextField, Tooltip} from "@material-ui/core";
import VerIcon from "@material-ui/icons/VisibilityRounded";
import {SimpleDialog} from "components/helpers/dialogs/simple.dialog";
import {CobranzasView} from "components/ventas/cobranzas/view/cobranzas.view";


// Valor de la opción que tilda/destilda todas las opciones de un filtro.
const TODAS_LAS_OPCIONES = '__todas__';

// El menú se ancla debajo del campo: por defecto se posiciona sobre el ítem seleccionado, con lo que al tildar
// o destildar todos el popup se movía y la lista se escroleaba sola.
const MENU_PROPS_FILTRO = {
    variant: 'menu',
    getContentAnchorEl: null,
    anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
    transformOrigin: {vertical: 'top', horizontal: 'left'}
};

// Los nombres completos no entran en el control, así que se muestra la cantidad de opciones tildadas.
const renderSeleccion = (opciones, plural) => seleccionadas => {
    if (seleccionadas.length === 0)
        return 'NINGUNO';

    if (seleccionadas.length === opciones.length)
        return 'TODOS';

    if (seleccionadas.length === 1) {
        const opcion = opciones.find(opcion => opcion.id === seleccionadas[0]);
        return opcion ? opcion.name : '';
    }

    return `${seleccionadas.length} ${plural}`;
};

// Filtro desplegable de selección múltiple, con una opción para tildar o destildar todas.
const FiltroMultiple = ({label, opciones, seleccionadas, plural, onChange, onClose}) => {
    const todasTildadas = opciones.length > 0 && seleccionadas.length === opciones.length;

    const onChangeSeleccion = event => {
        const seleccion = event.target.value;

        // La opción 'Todos' no es una opción más: tilda a todas o las destilda a todas.
        if (seleccion.includes(TODAS_LAS_OPCIONES))
            onChange(todasTildadas ? [] : opciones.map(opcion => opcion.id));
        else
            onChange(seleccion);
    };

    return (
        <TextField
            select
            label={label}
            className='mr-2'
            style={{minWidth: 160}}
            value={seleccionadas}
            onChange={onChangeSeleccion}
            InputLabelProps={{shrink: true}}
            SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: renderSeleccion(opciones, plural),
                onClose: onClose,
                MenuProps: MENU_PROPS_FILTRO
            }}
        >
            <MenuItem value={TODAS_LAS_OPCIONES}>
                <Checkbox
                    checked={todasTildadas}
                    indeterminate={seleccionadas.length > 0 && !todasTildadas}
                    size={'small'}
                />
                <ListItemText primary={'Todos'}/>
            </MenuItem>
            {opciones.map(opcion => (
                <MenuItem key={opcion.id} value={opcion.id}>
                    <Checkbox checked={seleccionadas.includes(opcion.id)} size={'small'}/>
                    <ListItemText primary={opcion.name}/>
                </MenuItem>
            ))}
        </TextField>
    )
};

// Orden en el que se muestran las cobranzas: primero las que requieren intervención del vendedor.
// Son también las opciones del filtro de estados.
const ORDEN_STATUS = ['P', 'R', 'O', 'G', 'A', 'S', 'I'];

const OPCIONES_STATUS = ORDEN_STATUS.map(status => ({id: status, name: getCobranzaStatusInfo(status).description}));

// Las pendientes todavía no se pueden trabajar, y las rendidas e ingresadas ya siguieron su curso.
const STATUS_POR_DEFECTO = ['R', 'O', 'G', 'A'];

const ordenDeStatus = status => {
    const orden = ORDEN_STATUS.indexOf(status);
    return orden === -1 ? ORDEN_STATUS.length : orden;
};

// Ordena por status (Pendiente, En Revisión, Observada, Guardada, Aprobada, Rendida, Ingresada) y, dentro de cada uno, de la más antigua a la más nueva.
const ordenarCobranzas = cobranzas => [...cobranzas].sort((cobranza1, cobranza2) =>
    (ordenDeStatus(cobranza1.status) - ordenDeStatus(cobranza2.status)) ||
    sortDate(cobranza1.fecharecibo, cobranza2.fecharecibo)
);

export class Generar extends Component {
    // Se declara antes que el state porque las columnas de la grilla lo referencian.
    // La grilla selecciona la fila al hacerle click, así que el botón corta la propagación.
    VerBtn = ({row}) => (
        <Tooltip title={'Ver cobranza'}>
            <IconButton onClick={event => {
                event.stopPropagation();
                this.onOpenInfoDialog(row);
            }}>
                <VerIcon fontSize={'small'}/>
            </IconButton>
        </Tooltip>
    );

    state = {
        fecha_desde: (new MomentUtils()).date(new Date()).subtract(30, 'days'),
        fecha_hasta: (new MomentUtils()).date(new Date()),
        // Filtro de vendedores: arranca con todos seleccionados y sin ninguno no se lista nada.
        vendedores_seleccionados: [],
        status_seleccionados: [...STATUS_POR_DEFECTO],
        cobranzas: {
            columns: [
                {name: 'id', title: 'ID'},
                {name: 'numrec', title: 'Recibo'},
                {name: 'fecharecibo', title: 'Fecha'},
                {name: 'razsoc', title: 'Cliente'},
                {name: 'vendedor', title: 'Vendedor'},
                {name: 'custom1', title: 'Estado'},
                {name: 'custom2', title: ' '}
            ],
            columnExtensions: [
                {columnName: 'id', width: 100, compare: sortNumeric, direction: 'desc'},
                {columnName: 'numrec', width: 100, compare: sortNumeric},
                {columnName: 'fecharecibo', width: 150, compare: sortDate},
                {columnName: 'vendedor', width: 250},
                {columnName: 'custom1', width: 150, component: CobranzaStatusFormatter},
                {columnName: 'custom2', width: 50, component: this.VerBtn, align: 'center'},
            ],
            data: [],
            selected: [],
            all: true
        },
        vendedores: [],
        pdfBlob: null,
        pdfUrl: '',
        mailDialogOpen: false,
        pdfDialogOpen: false,
        dialOpen: false,
        filterChequeElectro: false,
        infoDialog: {open: false}
    };

    MastersService = new MastersService(this.context);
    VendedoresService = new VendedoresService(this.context);
    ClientesService = new ClientesService(this.context);
    ListadosService = new ListadosService(this.context);
    EmailService = new EmailService(this.context);
    CobranzasService = new CobranzasService(this.context);

    // Los filtros desplegables consultan recién al cerrarse, para no pedir las cobranzas por cada opción
    // que se tilda. Marca que la selección cambió y todavía no se consultó.
    filtroSinConsultar = false;

    getCobranzas = () => {
        const {vendedores_seleccionados, status_seleccionados} = this.state;

        // Sin estados tildados, o sin vendedores para quien puede elegirlos, no hay nada para mostrar.
        const sinVendedores = this.puedeFiltrarPorVendedor() && vendedores_seleccionados.length === 0;

        if (sinVendedores || status_seleccionados.length === 0) {
            this.setState(prevState => ({cobranzas: {...prevState.cobranzas, data: []}}));
            return;
        }

        this.CobranzasService.getFiltered({
                desde: this.state.fecha_desde.format('DD/MM/YY'),
                hasta: this.state.fecha_hasta.format('DD/MM/YY'),
                statusIn: status_seleccionados,
                vendedores: vendedores_seleccionados,
                filterChequeElectro: this.state.filterChequeElectro
            },
            response => this.setState(prevState => prevState.cobranzas.data = ordenarCobranzas(response.data.result)),
            error => this.props.enqueueSnackbar(error, {variant: 'error'})
        );
    }

    getVendedores = () => {
        this.VendedoresService.getAll(
            response => {
                const vendedores = response.data.result;
                // Por defecto se listan las cobranzas de todos los vendedores.
                this.setState(
                    {vendedores: vendedores, vendedores_seleccionados: vendedores.map(vendedor => vendedor.id)},
                    this.getCobranzas
                );
            },
            error => this.props.enqueueSnackbar(error, {variant: 'error'})
        );
    }

    // Los vendedores y cobradores solo ven sus propias cobranzas, así que no eligen vendedor.
    puedeFiltrarPorVendedor = () => this.context.loggedUser.role === 'A' || this.context.loggedUser.role === 'S';

    componentDidMount() {
        // Al traer los vendedores se seleccionan todos y recién ahí se piden las cobranzas.
        if (this.puedeFiltrarPorVendedor())
            this.getVendedores();
        else
            this.getCobranzas();
    }

    onSelect = (field, selected) => this.setState(prevState => (prevState[field].selected = selected));
    onChange = (field, value) => {
        // Al cambiar un filtro cambia el listado, por lo que la selección previa deja de ser válida.
        this.setState(prevState => ({[field]: value, cobranzas: {...prevState.cobranzas, selected: []}}), () => {
            this.getCobranzas();
        });
    };

    getPDF = (fnSuccess = () => {
    }) => {
        if (this.state.cobranzas.selected.length === 0) {
            this.props.enqueueSnackbar("Debe seleccionar al menos una cobranza para rendir", {variant: 'error'})
            return false;
        }

        if (this.state.cobranzas.selected.length > 130) {
            this.props.enqueueSnackbar("El máximo de cobranzas a rendir es 130", {variant: 'error'})
            return false;
        }

        const noAprobadas = this.state.cobranzas.data.filter(
            cobranza => this.state.cobranzas.selected.includes(cobranza.id) && cobranza.status !== 'A'
        );

        if (noAprobadas.length > 0) {
            this.props.enqueueSnackbar("Solo se pueden rendir cobranzas aprobadas", {variant: 'error'})
            return false;
        }

        this.ListadosService.rendicionCobranzas(
            this.state.cobranzas.selected,
            file => this.setState({pdfBlob: file}, fnSuccess),
            error => this.props.enqueueSnackbar(error, {variant: 'error'})
        );
    };

    // Show PDF
    onOpenPdfDialog = () => this.setState({pdfDialogOpen: true});
    onClosePdfDialog = () => this.setState({pdfDialogOpen: false});

    onShowPDF = () => {
        this.getPDF(
            () => {
                const fileURL = URL.createObjectURL(this.state.pdfBlob);
                this.setState({pdfUrl: fileURL}, this.onOpenPdfDialog);
            }
        );
    };

    // Print PDF
    onPrintPDF = () => {
        this.getPDF(
            () => {
                const fileURL = URL.createObjectURL(this.state.pdfBlob);
                printJS(fileURL);
            }
        );
    };

    // Download PDF
    onDownloadPdf = () => {
        this.getPDF(
            () => {
                const fileURL = URL.createObjectURL(this.state.pdfBlob);
                var a = document.createElement("a");
                document.body.appendChild(a);
                a.style = "display: none";
                a.href = fileURL;
                a.download = `Rendición Cobranzas`;
                a.click();
                window.URL.revokeObjectURL(fileURL);

            }
        );
    };

    // Email
    onOpenMailDialog = () => this.setState({mailDialogOpen: true});

    dialActions = [
        {icon: <EmailIcon/>, name: 'Enviar por Email', action: this.onOpenMailDialog},
        {icon: <PrintIcon/>, name: 'Imprimir', action: this.onPrintPDF},
        {icon: <DownloadIcon/>, name: 'Descargar', action: this.onDownloadPdf},
        {icon: <ShowIcon/>, name: 'Ver en Pantalla', action: this.onShowPDF},
    ];

    onCloseMailDialog = () => this.setState({mailDialogOpen: false});

    sendMail = mailData => {
        let sendMail = () => {
            // Paso los parámetros a un formData para poder enviar el blob (attachment)
            var formData = new FormData();
            formData.append('attachment', this.state.pdfBlob);
            formData.append('data', JSON.stringify(mailData));

            this.EmailService.send(
                formData,
                response => {
                    this.onCloseMailDialog();
                    this.props.enqueueSnackbar('El email se envió correctamente', {variant: 'success'});
                },
                error => this.props.enqueueSnackbar(error, {variant: 'error'})
            );
        };

        if (mailData.attachFile)
            this.getPDF(() => sendMail());
        else
            sendMail();
    };

    onOpenInfoDialog = cobranza => this.setState({infoDialog: {open: true, cobranza: cobranza.id, cliente: cobranza.idcliente}});
    onCloseInfoDialog = () => this.setState({infoDialog: {open: false}});

    onDialOpen = () => this.setState({dialOpen: true});
    onDialClose = () => this.setState({dialOpen: false});

    onToggleFilterChequeElectro = () => this.onChange('filterChequeElectro', !this.state.filterChequeElectro);

    onChangeFiltro = (campo, seleccionadas) => {
        this.filtroSinConsultar = true;
        this.setState(prevState => ({
            [campo]: seleccionadas,
            cobranzas: {...prevState.cobranzas, selected: []}
        }));
    };

    onCloseFiltro = () => {
        if (!this.filtroSinConsultar)
            return;

        this.filtroSinConsultar = false;
        this.getCobranzas();
    };

    render() {
        const {
            fecha_desde, fecha_hasta, vendedores_seleccionados, status_seleccionados, cobranzas, vendedores, mailDialogOpen,
            pdfDialogOpen, pdfUrl, dialOpen, filterChequeElectro, infoDialog
        } = this.state;

        return (
            <>
                <Box>
                    <Grid container spacing={4}>
                        <Grid item container xs={12} spacing={1} alignItems={'flex-end'}>
                            <Grid item>
                                <DateRangeControl
                                    desde={fecha_desde}
                                    hasta={fecha_hasta}
                                    onChange={(desde, hasta) => this.setState({fecha_desde: desde, fecha_hasta: hasta}, this.getCobranzas)}
                                />
                            </Grid>
                            {this.puedeFiltrarPorVendedor() && (
                                <Grid item>
                                    <FiltroMultiple
                                        label={'Vendedores'}
                                        opciones={vendedores}
                                        seleccionadas={vendedores_seleccionados}
                                        plural={'vendedores'}
                                        onChange={seleccionadas => this.onChangeFiltro('vendedores_seleccionados', seleccionadas)}
                                        onClose={this.onCloseFiltro}
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <FiltroMultiple
                                    label={'Estados'}
                                    opciones={OPCIONES_STATUS}
                                    seleccionadas={status_seleccionados}
                                    plural={'estados'}
                                    onChange={seleccionadas => this.onChangeFiltro('status_seleccionados', seleccionadas)}
                                    onClose={this.onCloseFiltro}
                                />
                            </Grid>
                            <Grid item className={'ml-auto'}>
                                <Tooltip title={'Solo cheque electrónico'}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={filterChequeElectro}
                                                onChange={this.onToggleFilterChequeElectro}
                                            />
                                        }
                                        label="Solo CHE"
                                    />
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Box className={'mt-3'}>
                        <ReactGrid
                            title={`Cobranzas`}
                            height={-250}
                            columns={cobranzas.columns}
                            columnExtensions={cobranzas.columnExtensions}
                            getRowId={row => row.id}
                            rows={cobranzas.data}
                            showResultCount={true}
                            enableFiltering={true}
                            enableSorting={true}
                            enableSearch={true}
                            enableSelection={true}
                            selectedRows={cobranzas.selected}
                            onSelectionChange={selected => this.onSelect('cobranzas', selected)}
                            onDelete={selected => this.onSelect('cobranzas', [])}
                            showSelectAll={true}
                        />
                    </Box>
                    <Box className='niquel-fab-btn'>
                        <SpeedDial
                            ariaLabel="rendicion_cobranzas"
                            icon={<PDFIcon/>}
                            onBlur={this.onDialClose}
                            onClick={this.onDialOpen}
                            onClose={this.onDialClose}
                            onFocus={this.onDialOpen}
                            onMouseEnter={this.onDialOpen}
                            onMouseLeave={this.onDialClose}
                            open={dialOpen}
                        >
                            {this.dialActions.map(action => (
                                <SpeedDialAction
                                    key={action.name}
                                    icon={action.icon}
                                    tooltipTitle={action.name}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        action.action()
                                    }}
                                />
                            ))}
                        </SpeedDial>
                    </Box>
                </Box>
                <MailDialog
                    open={mailDialogOpen}
                    onConfirm={this.sendMail}
                    onCancel={this.onCloseMailDialog}
                    fileName={`Rendicion_Cobranzas.pdf`}
                    attachFile={true}
                    // to={cliente.email}
                    subject={`Rendición Cobranzas`}
                    body={`Estimado,  \n\nSe adjunta el listado de rendición de cobranzas.`}
                />
                <PDFDialog
                    open={pdfDialogOpen}
                    onClose={this.onClosePdfDialog}
                    pdfUrl={pdfUrl}
                    pdfName={`Rendicion_Cobranzas.pdf`}
                />
                <SimpleDialog
                    open={infoDialog.open}
                    onCancel={this.onCloseInfoDialog}
                    maxWidth={'lg'}
                    fullWidth={true}
                    title={'Cobranza'}
                    body={<CobranzasView inDialog match={{params: {cobranza: infoDialog.cobranza, cliente: infoDialog.cliente}}}/>}
                    cancelText={'Cerrar'}
                />
            </>
        )
    }
}


Generar.contextType = MainContext;
Generar = withSnackbar(Generar);
